import { Copy, FileDownload, ThumbsDown, ThumbsUp } from '@/assets/icons'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { FeedbackThumb } from '@/chat-common/schemas/feedback-schema'
import { copyAgentResponseToClipboard } from '@/agent/events/util/CopyAgentResponseToClipboard'
import { downloadAgentResponseAsDocx } from '@/agent/conversations/util/DownloadAgentConversation'
import { putResponseFeedback } from '@/agent/response_metadata/fetch/put-response-feedback'
import { AgentResponseMetadataActions } from '@/agent/response_metadata/store/slice'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationShortMs } from '@/constants/constants-ui'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { selectAgentResponseThumbRating } from '@/agent/response_metadata/store/selectors'
import { ClientAgentEventRead } from '@/agent/events/store/schemas'
import { selectPendingSkillForConversation } from '@/skills/store/selectors'
import { useIsConversationOwner } from '@/agent/conversations/hooks/useIsConversationOwner'

type AgentResponseActionsProps = {
  conversationId: string
  event: ClientAgentEventRead
}

/**
 * Agent Response Actions
 *
 * Actions for agent responses such as:
 * - Thumb Rating (up/down) to submit feedback
 * - Copy response to clipboard
 * - Download response as DOCX
 */
export default function AgentResponseActions(props: AgentResponseActionsProps) {
  const { conversationId, event } = props
  const { trackEvent } = useAnalytics()
  const dispatch = useAppDispatch()

  const eventId = event.id
  const responseThumbRating = useAppSelector((state: RootState) => selectAgentResponseThumbRating(state, { eventId: event.id }))
  const pendingSkill = useAppSelector((state: RootState) => selectPendingSkillForConversation(state, { conversationId }))

  // Non-owners (collaborators viewing someone else's chat) get read-only actions: Copy and Download only.
  const isOwner = useIsConversationOwner(conversationId)

  const handleThumbClick = (positive: boolean) => {
    const thumbRating = positive ? FeedbackThumb.UP : FeedbackThumb.DOWN

    // Submit initial feedback with empty text
    putResponseFeedback({ eventId, thumb: thumbRating, thumbText: '' })

    // Open dialog for text feedback
    dispatch(AgentResponseMetadataActions.openFeedbackDialog({ eventId, positive }))

    // Set the thumb rating in state
    dispatch(AgentResponseMetadataActions.setThumbRating({ eventId, thumb: thumbRating }))

    // Analytics
    trackEvent(positive ? AnalyticsEvent.ResponseThumbUp : AnalyticsEvent.ResponseThumbDown, {
      skill: pendingSkill?.skill ?? '',
      skillSource: pendingSkill?.skillSource ?? '',
    })
  }

  const handleCopyAgentResponse = async () => {
    try {
      await copyAgentResponseToClipboard(event)
      dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: 'Text copied to clipboard', durationMs: kToastDurationShortMs }))
    } catch (error) {
      dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: 'Failed to copy text', durationMs: kToastDurationShortMs }))
      console.error('Error copying text:', error)
    }
  }

  return (
    <div className="flex gap-x-2 items-center text-brand-neutral-600 text-xs">
      {isOwner && (
        <>
          <button
            title="This response was helpful"
            className={`p-1 rounded-full hover:text-brand-neutral-700 hover:bg-brand-neutral-100 ${
              responseThumbRating === FeedbackThumb.UP ? 'bg-brand-neutral-100' : ''
            }`}
            onClick={() => handleThumbClick(true)}
          >
            <ThumbsUp className="h-5 w-5" />
          </button>
          <button
            title="This response was not helpful"
            className={`p-1 rounded-full hover:text-brand-neutral-700 hover:bg-brand-neutral-100 ${
              responseThumbRating === FeedbackThumb.DOWN ? 'bg-brand-neutral-100' : ''
            }`}
            onClick={() => handleThumbClick(false)}
          >
            <ThumbsDown className="h-5 w-5" />
          </button>
        </>
      )}
      <button
        title="Copy response to clipboard"
        className="p-1 rounded-full hover:text-brand-neutral-700 hover:bg-brand-neutral-100"
        onClick={() => {
          trackEvent(AnalyticsEvent.CopyChatResponse)
          handleCopyAgentResponse()
        }}
      >
        <Copy className="h-5 w-5" />
      </button>
      <button
        title="Download response as a DOCX file"
        className="p-1 rounded-full hover:text-brand-neutral-700 hover:bg-brand-neutral-100"
        onClick={async () => {
          trackEvent(AnalyticsEvent.DownloadChatResponseToDocx)
          await downloadAgentResponseAsDocx(conversationId, event)
        }}
      >
        <FileDownload className="h-5 w-5" />
      </button>
    </div>
  )
}
